import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RotateCcw, PenLine } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const SIG_FIELDS = [["name", "Name"], ["designation", "Designation"], ["company", "Company"], ["phone", "Phone"], ["email", "Email"]];

// Editable catalogue email: template parts filled live until the rep edits a field; signature from the account
export function EmailComposer({ brand, visitorName, eventName, catalogues, onChange }) {
  const base = useQuery({ queryKey: ["ld-email-draft", brand], queryFn: async () => (await api.get("/lead-desk/email-draft", { params: { brand } })).data });
  const [edited, setEdited] = useState({});
  const [draft, setDraft] = useState({ subject: "", intro: "", closing: "", signature: {} });

  const fill = useMemo(() => (s) => (s || "").replace(/\{first_name\}/g, (visitorName || "").trim().split(/\s+/)[0] || "[First name]").replace(/\{name\}/g, visitorName || "[Visitor name]").replace(/\{event\}/g, eventName || "[Exhibition]"), [visitorName, eventName]);

  useEffect(() => {
    if (!base.data) return;
    setDraft((d) => ({
      subject: edited.subject ? d.subject : fill(base.data.subject),
      intro: edited.intro ? d.intro : fill(base.data.intro),
      closing: edited.closing ? d.closing : fill(base.data.closing),
      signature: { ...base.data.signature, ...Object.fromEntries(Object.entries(d.signature).filter(([k]) => edited[`sig.${k}`])) },
    }));
  }, [base.data, fill, edited]);

  useEffect(() => { onChange(draft); }, [draft, onChange]);

  const set = (k, v) => { setEdited((e) => ({ ...e, [k]: true })); setDraft((d) => ({ ...d, [k]: v })); };
  const setSig = (k, v) => { setEdited((e) => ({ ...e, [`sig.${k}`]: true })); setDraft((d) => ({ ...d, signature: { ...d.signature, [k]: v } })); };
  const reset = () => { setEdited({}); if (base.data) setDraft({ subject: fill(base.data.subject), intro: fill(base.data.intro), closing: fill(base.data.closing), signature: { ...base.data.signature } }); };
  const dirty = Object.keys(edited).length > 0;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3 text-xs" data-testid="ld-composer">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1"><PenLine className="h-3 w-3" /> Email — edit anything before sending</p>
        {dirty && <button type="button" onClick={reset} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="ld-composer-reset"><RotateCcw className="h-3 w-3" /> Reset to template</button>}
      </div>
      <div className="space-y-1"><Label className="text-[11px] text-slate-500">Subject</Label>
        <Input value={draft.subject} onChange={(e) => set("subject", e.target.value)} className="h-8 text-xs bg-white font-semibold" data-testid="ld-email-subject" /></div>
      <Textarea value={draft.intro} onChange={(e) => set("intro", e.target.value)} rows={6} className="text-xs bg-white resize-y" data-testid="ld-email-intro" />
      <div className="rounded border border-dashed border-slate-300 bg-white px-3 py-2 text-slate-600" data-testid="ld-email-catalogue-block">
        {catalogues.length ? catalogues.map((c) => <p key={c.id} className="flex items-center gap-2"><span className="inline-block rounded bg-brand text-white text-[10px] font-bold px-2 py-0.5">View catalogue</span>{c.name}</p>) : <p className="text-slate-400">[Catalogue buttons appear here — tick products in step 2]</p>}
      </div>
      <Textarea value={draft.closing} onChange={(e) => set("closing", e.target.value)} rows={6} className="text-xs bg-white resize-y" data-testid="ld-email-closing" />
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Signature · "Team UniVicoustic" by default, add your own details if you wish</p>
        <div className="grid grid-cols-2 gap-1.5">
          {SIG_FIELDS.map(([k, label]) => (
            <Input key={k} value={draft.signature[k] || ""} onChange={(e) => setSig(k, e.target.value)} placeholder={label} className={`h-8 text-xs bg-white ${k === "company" ? "col-span-2" : ""}`} data-testid={`ld-sig-${k}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
